import React, { useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { dueLabel } from '../tasks/deriveTasks';
import { ClientWork } from '../tasks/types';
import { BAND_LABELS, INK, TONES } from '../ui';
import { chipLabel, InboxGroup } from './inboxModel';

/** Rows shown per band before "Show more"; admins can have hundreds of clients in a band. */
const BAND_PAGE = 25;

const ClientRow: React.FC<{ item: ClientWork; selected: boolean; onSelect: () => void; now: Date; showCoach: boolean }> = ({ item, selected, onSelect, now, showCoach }) => {
  const { client, tasks } = item;
  const shown = tasks.slice(0, 3);
  // In the success-updates segment a row's one task is always the same, so its detail ("Last update 2 Sep") says more.
  const onlySuccess = tasks.every((task) => task.kind === 'success');
  const due = dueLabel(tasks[0], now) || BAND_LABELS[item.urgency];
  return (
    <Box component="li" sx={{ '& + &': { borderTop: `1px solid ${INK.faint}` } }}>
      <ButtonBase
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '6px 12px',
          px: 2,
          py: 1.25,
          textAlign: 'left',
          bgcolor: selected ? '#EEF3FE' : 'transparent',
          '&:hover': { bgcolor: selected ? '#EEF3FE' : '#F8FAFC' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        }}
      >
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: selected ? 'primary.dark' : INK.main }} noWrap>
            {client.name}
          </Typography>
          <Typography sx={{ fontSize: 13, color: INK.muted }} noWrap>
            {[client.programmeLabel, client.week ? `week ${client.week}` : null, showCoach ? client.coachName : null].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        {onlySuccess ? (
          <Typography component="span" sx={{ flex: '1 1 280px', fontSize: 13, color: INK.body }}>
            {tasks[0].detail}
          </Typography>
        ) : (
          <Box sx={{ flex: '1 1 280px', display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {shown.map((task) => (
              <Box
                key={task.id}
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 24, px: 1, border: `1px solid ${INK.hairline}`, borderRadius: '6px', fontSize: 12, color: INK.body, bgcolor: '#FFFFFF' }}
              >
                <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: TONES[task.urgency].dot }} />
                {chipLabel(task)}
              </Box>
            ))}
            {tasks.length > shown.length ? (
              <Box component="span" sx={{ fontSize: 12, color: INK.muted, alignSelf: 'center' }}>
                +{tasks.length - shown.length}
              </Box>
            ) : null}
          </Box>
        )}
        <Typography component="span" sx={{ width: 88, textAlign: 'right', fontSize: 13, fontWeight: 600, color: TONES[item.urgency].color }}>
          {due}
        </Typography>
      </ButtonBase>
    </Box>
  );
};

/** Clients in labelled groups (urgency bands, or the success-update split), one row each. `showCoach` names each lead coach (team views). */
export const InboxList: React.FC<{
  groups: InboxGroup[];
  selectedId: string | null;
  onSelect: (clientId: string) => void;
  now: Date;
  showCoach?: boolean;
}> = ({ groups, selectedId, onSelect, now, showCoach = true }) => {
  const [limits, setLimits] = useState<Record<string, number>>({});
  return (
    <>
      {groups.map(({ key, label, color, items }, index) => {
        const limit = limits[key] ?? BAND_PAGE;
        return (
          <Box key={key} component="section" aria-label={label}>
            <Typography
              component="h2"
              sx={{
                m: 0,
                px: 2,
                py: 1.25,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color,
                bgcolor: '#F8FAFC',
                borderTop: index === 0 ? 0 : `1px solid ${INK.hairline}`,
                borderBottom: `1px solid ${INK.faint}`,
              }}
            >
              {label} · {items.length}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {items.slice(0, limit).map((item) => (
                <ClientRow
                  key={item.client.clientId}
                  item={item}
                  now={now}
                  showCoach={showCoach}
                  selected={item.client.clientId === selectedId}
                  onSelect={() => onSelect(item.client.clientId)}
                />
              ))}
            </Box>
            {items.length > limit ? (
              <ButtonBase
                onClick={() => setLimits((current) => ({ ...current, [key]: limit + BAND_PAGE }))}
                sx={{ width: '100%', justifyContent: 'flex-start', px: 2, height: 40, borderTop: `1px solid ${INK.faint}`, fontSize: 13, fontWeight: 600, color: 'primary.main' }}
              >
                Show {Math.min(BAND_PAGE, items.length - limit)} more of {items.length - limit}
              </ButtonBase>
            ) : null}
          </Box>
        );
      })}
    </>
  );
};
